import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../api';
import { offerTypeFromSlug, extractIdFromSlug, buildSlug } from '../utils/slug';
import { imageFallback } from '../utils/env';
import { robotsForOffer } from '../utils/indexable';
import { useWishlist } from '../utils/wishlist';
import OfferCard from '../components/common/OfferCard';

function ensureProtocol(link) {
  if (!link || link === '#') return '#';
  if (/^https?:\/\//i.test(link)) return link;
  return `https://${link}`;
}

function renderInline(text, keyOffset = 0) {
  if (!text) return null;
  const regex = /\*\*(.+?)\*\*|\*([^*\n]+?)\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|https?:\/\/[^\s<>"]+/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = keyOffset;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) parts.push(<strong key={key++}>{match[1]}</strong>);
    else if (match[2] !== undefined) parts.push(<em key={key++}>{match[2]}</em>);
    else if (match[3] !== undefined) parts.push(<a key={key++} href={ensureProtocol(match[4])} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1' }}>{match[3]}</a>);
    else parts.push(<a key={key++} href={ensureProtocol(match[0])} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1' }}>{match[0]}</a>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function renderDescription(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('## ')) return <h2 key={i} style={{ fontSize: '1.15rem', fontWeight: 700, margin: '12px 0 4px', color: '#1e293b' }}>{renderInline(line.slice(3), i * 100)}</h2>;
    if (line.trim() === '') return <div key={i} style={{ height: '0.5em' }} />;
    return <p key={i} style={{ margin: '0 0 6px', lineHeight: 1.7, color: '#475569' }}>{renderInline(line, i * 100)}</p>;
  });
}

export default function OfferDetailPage() {
  const { state } = useLocation();
  const { typeSlug, slug } = useParams();
  const type = offerTypeFromSlug(typeSlug);
  const id = extractIdFromSlug(slug);
  const navigate = useNavigate();
  const [offer, setOffer] = useState(state?.offer || null);
  const [loading, setLoading] = useState(!state?.offer);
  const [error, setError] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [relatedOffers, setRelatedOffers] = useState([]);
  const { savedIds, toggleSave } = useWishlist();

  function handleCopyCode() {
    navigator.clipboard?.writeText(offer.couponCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1500);
  }

  useEffect(() => {
    if (!offer) {
      setLoading(true);
      fetch(`${API_ENDPOINTS.OFFERS}/${type}/${id}`)
        .then(res => {
          if (!res.ok) throw new Error('Offer not found');
          return res.json();
        })
        .then(data => { setOffer(data); setLoading(false); })
        .catch(err => { setError(err.message); setLoading(false); });
    }
  }, []);

  // Cross-links each offer page to a handful of same-category offers, so
  // Google's crawler has a real internal link path into pages that would
  // otherwise only be reachable via the sitemap (Semrush flagged 146 such
  // "orphaned" pages — sitemap-only URLs with no internal link pointing at
  // them, which is also the profile GSC shows as "Discovered - not indexed").
  useEffect(() => {
    if (!offer?.type) return;
    fetch(`${API_ENDPOINTS.OFFERS}?category=${encodeURIComponent(offer.type)}&limit=8`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data) return;
        const list = data.offers || data;
        setRelatedOffers(list.filter(o => o.id !== offer.id).slice(0, 4));
      })
      .catch(() => {});
  }, [offer?.id, offer?.type]);

  const formattedDate = offer?.createdAt
    ? new Date(offer.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="App">
      <header className="modern-header">
        <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <button className="admin-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </header>

      <div style={{ maxWidth: 800, margin: '32px auto', padding: '0 16px 60px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#6366f1', fontSize: '1.1rem', fontWeight: 600 }}>
            <div className="top-loading-bar" />
            Loading offer...
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            {/*
              Same reason as the blog not-found screen: unknown paths are served
              as the app shell with a 200 status, so an expired offer URL lands
              here successfully. Marking it noindex stops Google recording a
              soft 404. Expired offers are common, so this fires often.
            */}
            <Helmet>
              <title>Offer not found | OfferLelo</title>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <p style={{ color: '#ef4444', fontSize: '1.1rem', marginBottom: 20 }}>Offer not found</p>
            <button onClick={() => navigate('/offers')} className="card-link" style={{ display: 'inline-block' }}>
              Browse All Offers
            </button>
          </div>
        )}

        {offer && !loading && (
          <>
            <Helmet>
              <title>{offer.store ? `${offer.title} on ${offer.store} — Offer & Deal | OfferLelo` : `${offer.title} | OfferLelo`}</title>
              <meta name="description" content={`${offer.store ? `${offer.store} offer: ` : ''}${offer.description ? offer.description.substring(0, 150).replace(/\n/g, ' ') : offer.title}${Number(offer.offerPercentage) > 0 ? ` — Save ${offer.offerPercentage}% today on OfferLelo.` : ''}`} />
              <meta name="keywords" content={`${offer.store ? `${offer.store} offer, ${offer.store} deal, ${offer.store} discount, ${offer.store} coupon, ` : ''}${offer.type || ''} offers, online shopping India, OfferLelo`} />
              {/*
                Indexability is decided per offer, at render time, from the
                offer's own data — see src/utils/indexable.js for the rule and
                the reasoning behind the thresholds.

                Previously this was a blanket `noindex, follow` on every offer
                page, which was correct when every title was a supplier brand
                fragment, but it also excluded the ~94 offers that do have a
                real product name and a full description and can rank on their
                own ("Havells Bt5100C Rechargeable Beard Trimmer").

                Doing this at render time rather than at build time is what
                removes the rebuild requirement: an offer published after the
                last build is served the SPA shell with HTTP 200 and rendered
                client-side, so it gets the right robots tag immediately.

                Users are unaffected either way, and the og:/twitter: tags below
                keep social sharing intact for every offer.

                NOTE: the backend sitemap (Spring Boot /sitemap.xml) still lists
                ALL offer URLs. Listing a noindex page in a sitemap is a
                conflicting signal — the backend should apply this same rule and
                list only indexable offers. Blog + static + store URLs must stay.
              */}
              <meta name="robots" content={robotsForOffer(offer)} />
              <meta property="og:title" content={offer.store ? `${offer.title} on ${offer.store} — Offer & Deal | OfferLelo` : `${offer.title} | OfferLelo`} />
              <meta property="og:description" content={offer.description ? offer.description.substring(0, 160).replace(/\n/g, ' ') : offer.title} />
              <meta property="og:type" content="product" />
              <meta property="og:url" content={`https://www.offerlelo.com/offers/${typeSlug}/${buildSlug(offer.title, id)}`} />
              <meta property="og:site_name" content="OfferLelo" />
              <meta property="og:locale" content="en_IN" />
              {offer.image && <meta property="og:image" content={offer.image} />}
              {offer.currentPrice && <meta property="product:price:amount" content={offer.currentPrice} />}
              <meta property="product:price:currency" content="INR" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={offer.store ? `${offer.title} on ${offer.store} | OfferLelo` : `${offer.title} | OfferLelo`} />
              <meta name="twitter:description" content={offer.description ? offer.description.substring(0, 160).replace(/\n/g, ' ') : offer.title} />
              {offer.image && <meta name="twitter:image" content={offer.image} />}
              <link rel="canonical" href={`https://www.offerlelo.com/offers/${typeSlug}/${buildSlug(offer.title, id)}`} />
              <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                "name": offer.title,
                "description": offer.description ? offer.description.substring(0, 200).replace(/\n/g, ' ') : offer.title,
                "image": offer.image || 'https://www.offerlelo.com/logo192.png',
                ...(offer.store ? { "brand": { "@type": "Brand", "name": offer.store } } : {}),
                "category": offer.type || 'Offers',
                "offers": {
                  "@type": "Offer",
                  "priceCurrency": "INR",
                  "price": offer.currentPrice || offer.actualPrice || '0',
                  "availability": "https://schema.org/InStock",
                  "url": ensureProtocol(offer.link),
                  ...(offer.store ? { "seller": { "@type": "Organization", "name": offer.store } } : {}),
                  "areaServed": { "@type": "Country", "name": "India" }
                }
              })}</script>
              <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.offerlelo.com" },
                  { "@type": "ListItem", "position": 2, "name": "Offers", "item": "https://www.offerlelo.com/offers" },
                  { "@type": "ListItem", "position": 3, "name": offer.type, "item": `https://www.offerlelo.com/offers?category=${typeSlug}` },
                  { "@type": "ListItem", "position": 4, "name": offer.title, "item": `https://www.offerlelo.com/offers/${typeSlug}/${buildSlug(offer.title, id)}` }
                ]
              })}</script>
            </Helmet>
          <div className="offer-detail-card">
            <div className="offer-detail-img-wrap">
              <img
                src={offer.image || '/default-offer.svg'}
                alt={offer.title}
                className="offer-detail-img"
                width="800"
                height="500"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                onError={imageFallback('/default-offer.svg')}
              />
            </div>
            <div className="offer-detail-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                {offer.type && <span className="card-category-tag">{offer.type}</span>}
                {offer.store && <span className="card-store-badge">from {offer.store}</span>}
              </div>
              <h1 className="offer-detail-title">{offer.title}</h1>
              {(offer.currentPrice || offer.actualPrice) && (
                <div className="offer-detail-price-row">
                  {offer.currentPrice && <span className="offer-detail-price-current">₹{offer.currentPrice}</span>}
                  {offer.actualPrice && <span className="offer-detail-price-mrp">₹{offer.actualPrice}</span>}
                  {Number(offer.offerPercentage) > 0 && <span className="offer-detail-price-off">{offer.offerPercentage}% OFF</span>}
                </div>
              )}
              {offer.description && <div className="offer-detail-desc">{renderDescription(offer.description)}</div>}
              {formattedDate && <p className="offer-detail-date">Posted on {formattedDate}</p>}
              {offer.couponCode && (
                <div className="offer-detail-coupon">
                  <span className="offer-detail-coupon-label">🏷 Use code</span>
                  <span className="offer-detail-coupon-code">{offer.couponCode}</span>
                  <button type="button" className="offer-detail-coupon-copy-btn" onClick={handleCopyCode}>
                    {codeCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
              <a
                href={ensureProtocol(offer.link)}
                target="_blank"
                rel="noopener noreferrer"
                className="offer-detail-cta"
              >
                Get Deal →
              </a>

              <div className="blog-detail-share">
                <span>Share this offer:</span>
                <button className="share-btn" onClick={() => navigator.clipboard.writeText(window.location.href)}>
                  📋 Copy Link
                </button>
                <button className="share-btn" onClick={() => window.open(
                  `https://wa.me/?text=${encodeURIComponent(offer.title + ' ' + window.location.href)}`,
                  '_blank'
                )}>
                  💬 WhatsApp
                </button>
                <button className="share-btn" onClick={() => window.open(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
                  '_blank'
                )}>
                  📘 Facebook
                </button>
              </div>
            </div>
          </div>

          {relatedOffers.length > 0 && (
            <div style={{ marginTop: 40 }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>
                Related {offer.type} Offers
              </h2>
              <div className="grid-container">
                {relatedOffers.map(o => (
                  <OfferCard key={o.id} item={o} savedIds={savedIds} onToggleSave={toggleSave} />
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
